import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/* Public Founding banner — stealth scarcity.
 * NEVER exposes numerical remaining/claimed counts. Uses qualitative copy
 * that shifts based on how many seats are actually left server-side. */
export default function FoundingBanner({ variant = "landing" }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/membership/founding"); setS(data); }
      catch { /* noop */ }
    })();
  }, []);
  if (!s) return null;
  const soldOut = s.remaining === 0;

  // Qualitative status — no numbers exposed. Tuned so the copy tightens
  // as availability shrinks.
  const pctRemaining = s.remaining / (s.limit || 100);
  let headline;
  let sub;
  if (soldOut) {
    headline = "Enrollment Closed";
    sub = "The Founding Producer cohort has been filled. Standard BeatCredsi membership is now live.";
  } else if (pctRemaining <= 0.1) {
    headline = "Final Founding Spots Available";
    sub = "Enrollment closing shortly. Founding rate locks in for life the moment you claim your seat.";
  } else if (pctRemaining <= 0.25) {
    headline = "Enrollment Closing Soon";
    sub = "Founding Producer seats are nearly filled. Lock the lifetime rate before the window shuts.";
  } else if (pctRemaining <= 0.5) {
    headline = "Founding Enrollment Open";
    sub = "Founding rate locks in for life — even after standard membership activates.";
  } else {
    headline = "Founding Producer Cohort — Now Open";
    sub = "Lock in the lifetime founding rate. Standard BeatCredsi membership activates soon after.";
  }

  if (variant === "compact") {
    return (
      <div data-testid="founding-compact" className="inline-flex items-center gap-3 px-4 py-2 border border-amber-500/30 bg-amber-500/5">
        <span className="w-2 h-2 bg-amber-500 animate-pulse" />
        <span className="text-xs uppercase tracking-widest font-semibold text-amber-500">Founding Producer</span>
        <span className="text-xs text-neutral-400" data-testid="founding-status">
          {soldOut ? "Enrollment Closed" : headline}
        </span>
      </div>
    );
  }

  return (
    <div data-testid="founding-banner" className="border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent p-8 lg:p-10">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="max-w-xl">
          <div className="inline-flex items-center gap-2 mb-4">
            <span className="w-2 h-2 bg-amber-500 animate-pulse" />
            <span className="text-overline">Limited — Founding Producer</span>
          </div>
          <h3 className="font-display text-2xl lg:text-4xl font-medium tracking-tight text-white">
            Founding producers lock in <span className="text-amber-500">${s.price}/mo for life.</span>
          </h3>
          <p className="mt-3 text-neutral-400 text-sm lg:text-base leading-relaxed">{sub}</p>
        </div>
        <div className="text-right shrink-0 min-w-[180px]">
          <div className="font-display text-lg sm:text-xl font-medium text-amber-500 leading-tight" data-testid="founding-headline">
            {headline}
          </div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mt-1">
            {soldOut ? "Cohort Sealed" : "Lifetime Rate"}
          </div>
        </div>
      </div>
      {!soldOut && (
        <div className="mt-6 h-1.5 bg-white/5 relative overflow-hidden">
          <div className="absolute inset-y-0 left-0 bg-amber-500 animate-pulse" style={{ width: "100%" }} />
        </div>
      )}
    </div>
  );
}
